/**
 * Vorlesen-Bedienung (Auftraggeber-Bedienkonzept 13.06.):
 *  - <TtsBar/>  : volle Knopfreihe im Drawer-Modus-Panel (Audio-Schalter ·
 *    Play/Pause · Überspringen · Kapitel/Durchlesen).
 *  - <TtsQuick/>: permanenter Schnell-Knopf im Kapitelkopf — „schnell leise machen".
 *
 * Konzept 13.06.:
 *  - AUDIO-HAUPTSCHALTER (An/Aus): nur wenn AN sind alle Vorlese-Interaktionen
 *    (Play, Tipp-Gesten) aktiv. AUS = wie Stopp + Tippen löst nichts aus.
 *  - PLAY-KNOPF (Einzeltipp wechselt): gestoppt → Play (Abschnittsanfang);
 *    spielt → Pause (Stelle gemerkt); pausiert → weiter an der Stelle.
 *  - DOPPELTIPP auf den Play-Knopf → STOPP (Reset auf Abschnittsanfang).
 * Auf Touch gibt es kein title-Hover → sichtbare Wort-Labels + aria-label.
 */
import {
  ttsPlaying,
  ttsPaused,
  ttsWaiting,
  ttsLoading,
  ttsModus,
  ttsAudioOn,
  ttsAuto,
  setTtsModus,
  toggleTtsAudioOn,
  toggleTtsAuto,
  ttsPlayKlick,
  ttsStoppKnopf,
  skipSegment,
  ttsSpeedPos,
  setTtsSpeedPos,
  rateFromPos,
  SPEED_MIN_POS,
  SPEED_MAX_POS,
  SPEED_STEP,
} from "./player";
import { getToken } from "../state/session";
import { useRef } from "preact/hooks";

const SKIP_LABEL = "Diesen vorgelesenen Teil überspringen";
const DBL_MS = 250; // Einzel- vs. Doppeltipp-Fenster (13.06.)

function playGlyph(): string {
  if (ttsLoading.value) return "⏳";
  if (ttsWaiting.value) return "▶"; // wartet auf „weiter"
  if (ttsPlaying.value) return "⏸"; // spielt → Einzeltipp pausiert
  return "▶"; // gestoppt/pausiert → Einzeltipp startet/setzt fort
}
function playLabel(): string {
  if (ttsLoading.value) return "Audio wird erzeugt …";
  if (ttsWaiting.value) return "Weiter vorlesen (Doppeltipp = Stopp)";
  if (ttsPlaying.value) return "Pause — Stelle wird gemerkt (Doppeltipp = Stopp)";
  if (ttsPaused.value) return "Weiterlesen an der gemerkten Stelle (Doppeltipp = Stopp)";
  return "Vorlesen ab diesem Abschnitt (Doppeltipp = Stopp)";
}

/** Einzel-/Doppeltipp am selben Knopf trennen: 1. Tipp wartet DBL_MS, kommt ein
 *  2. Tipp → onDouble (Stopp), sonst onSingle (Play/Pause). Beim verzögerten
 *  Feuern erneut prüfen, ob Audio noch AN ist (Schalter inzwischen umgelegt). */
function useDoubleTap(onSingle: () => void, onDouble: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
      if (ttsAudioOn.value) onDouble();
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      if (ttsAudioOn.value) onSingle();
    }, DBL_MS);
  };
}

/** Audio An/Aus-Hauptschalter (13.06.). Aus = stumm + Tipp-Gesten deaktiviert. */
function AudioSchalter({ compact = false }: { compact?: boolean }) {
  const on = ttsAudioOn.value;
  const label = on ? "Audio ist an — tippen zum Ausschalten" : "Audio ist aus — tippen zum Einschalten";
  return (
    <button
      type="button"
      class={`ap-audio${on ? " an" : ""}${compact ? " tts-quick" : ""}`}
      aria-label={label}
      aria-pressed={on}
      title={label}
      onClick={toggleTtsAudioOn}
    >
      <span aria-hidden="true">{on ? "🔊" : "🔇"}</span>
      {compact ? null : <span class="ap-lbl">{on ? "Audio an" : "Audio aus"}</span>}
    </button>
  );
}

function speedLabel(pos: number): string {
  return pos === 0 ? "0" : (pos > 0 ? "+" : "−") + String(Math.abs(pos)).replace(".", ",");
}
function speedTip(pos: number): string {
  if (pos === 0) return "Vorlese-Tempo: normal — Mitte ist normal, Tonhöhe bleibt gleich.";
  const rate = rateFromPos(pos).toFixed(2).replace(".", ",");
  return `Vorlese-Tempo: ${rate}× (${pos > 0 ? "schneller" : "langsamer"}) — Mitte ist normal, Tonhöhe bleibt gleich.`;
}
/** Tempo-Schieberegler (Mitte = 0 = normal). Tempo ändern ≠ Tonhöhe ändern. */
function SpeedSlider() {
  const pos = ttsSpeedPos.value;
  return (
    <span class="ap-speed" title={speedTip(pos)}>
      <span class="ap-spd-ico" aria-hidden="true">🐢</span>
      <input
        type="range"
        class="ap-spd-range"
        min={SPEED_MIN_POS}
        max={SPEED_MAX_POS}
        step={SPEED_STEP}
        value={pos}
        aria-label="Vorlese-Tempo (Mitte = normal, Tonhöhe bleibt gleich)"
        onInput={(e) => setTtsSpeedPos(parseFloat((e.currentTarget as HTMLInputElement).value))}
      />
      <span class="ap-spd-ico" aria-hidden="true">🐇</span>
      <span class="ap-spd-val">{speedLabel(pos)}</span>
    </span>
  );
}

export function TtsBar() {
  if (!getToken()) return null;
  const audioOn = ttsAudioOn.value;
  const waiting = !!ttsWaiting.value;
  const playing = ttsPlaying.value && !waiting;
  const paused = ttsPaused.value && !playing && !waiting;
  const onPlayTap = useDoubleTap(ttsPlayKlick, ttsStoppKnopf);
  return (
    <div class="ap-row" role="toolbar" aria-label="Vorlesen-Steuerung">
      <AudioSchalter />
      <button
        type="button"
        class={`ap-play${playing ? " spielt" : ""}${waiting ? " wartet" : ""}${paused ? " pausiert" : ""}`}
        aria-label={playLabel()}
        title={playLabel()}
        disabled={!audioOn}
        onClick={onPlayTap}
      >
        <span aria-hidden="true">{playGlyph()}</span>
        <span class="ap-lbl">{waiting ? "Weiter" : playing ? "Pause" : "Vorlesen"}</span>
      </button>
      <button
        type="button"
        class="ap-skip"
        aria-label={SKIP_LABEL}
        title={SKIP_LABEL}
        disabled={!audioOn || (!playing && !waiting)}
        onClick={skipSegment}
      >
        <span aria-hidden="true">⏭</span>
        <span class="ap-lbl">Weiter-Teil</span>
      </button>
      <button
        type="button"
        class={`ap-mode ${ttsModus.value === "durch" ? "ap-md active" : "ap-mk active"}`}
        aria-label={
          ttsModus.value === "kapitel"
            ? "Modus: Kapitel — stoppt an jedem Weiter-Punkt. Tippen schaltet auf Durchlesen um."
            : "Modus: Durchlesen — blättert automatisch weiter. Tippen schaltet auf Kapitel um."
        }
        title={
          ttsModus.value === "kapitel"
            ? "Modus: Kapitel — stoppt an jedem Weiter-Punkt. Tippen schaltet auf Durchlesen um."
            : "Modus: Durchlesen — blättert automatisch weiter. Tippen schaltet auf Kapitel um."
        }
        onClick={() => setTtsModus(ttsModus.value === "kapitel" ? "durch" : "kapitel")}
      >
        <span aria-hidden="true">{ttsModus.value === "kapitel" ? "⇥" : "⇉"}</span>
        <span class="ap-lbl">{ttsModus.value === "kapitel" ? "Kapitel" : "Durchlesen"}</span>
      </button>
      <button
        type="button"
        class={`ap-auto${ttsAuto.value ? " an" : ""}`}
        aria-label={
          ttsAuto.value
            ? "Auto-Vorlesen ist an — neue Kapitel werden automatisch vorgelesen. Tippen schaltet aus."
            : "Auto-Vorlesen ist aus. Tippen schaltet ein — neue Kapitel werden dann automatisch vorgelesen."
        }
        aria-pressed={ttsAuto.value}
        title={
          ttsAuto.value
            ? "Auto-Vorlesen ist an — neue Kapitel werden automatisch vorgelesen. Tippen schaltet aus."
            : "Auto-Vorlesen ist aus. Tippen schaltet ein — neue Kapitel werden dann automatisch vorgelesen."
        }
        onClick={toggleTtsAuto}
      >
        <span aria-hidden="true">{ttsAuto.value ? "🔁" : "↻"}</span>
        <span class="ap-lbl">{ttsAuto.value ? "Auto an" : "Auto aus"}</span>
      </button>
      <SpeedSlider />
    </div>
  );
}

/* Kopfleisten-Bedienung (Auftraggeber 2026-06-18): Play/Pause · Stop · Tempo (−/🏃/+).
 * KEIN Mute-Knopf mehr — Play schaltet das Audio ein und liest automatisch durch
 * (Durchlesen-Modus); Stop schaltet hart aus (stoppt + Tippen liest dann nicht). Der
 * Tempo-Regler steht statt des früheren Mute-Knopfs. player.ts bleibt unangetastet. */
function headerPlay(): void {
  if (!ttsPlaying.value) {
    if (!ttsAudioOn.value) toggleTtsAudioOn(); // Audio aktivieren — sonst tut Play nichts
    setTtsModus("durch"); // Play = automatisch weiterlesen
  }
  ttsPlayKlick();
}
function headerStop(): void {
  if (ttsAudioOn.value) toggleTtsAudioOn(); // schaltet hart aus + stoppt
  else ttsStoppKnopf();
}

/* Tempo als EIN Knopf (Auftraggeber 2026-06-18: spart Platz). Kuratierte Stufen,
 * Tippen schaltet durch (Wrap): 1,0× → 1,2× → 1,4× → 0,8× → 1,0× … */
const SPEED_CYCLE = [0, 0.5, 1, -0.5];
function headerSpeedCycle(): void {
  const cur = ttsSpeedPos.value;
  let i = SPEED_CYCLE.indexOf(cur);
  if (i < 0) {
    let best = 0, bd = Infinity;
    SPEED_CYCLE.forEach((p, k) => { const d = Math.abs(p - cur); if (d < bd) { bd = d; best = k; } });
    i = best;
  }
  setTtsSpeedPos(SPEED_CYCLE[(i + 1) % SPEED_CYCLE.length]);
}

/** Schnell-Bedienung in der Kopfleiste: Audio-Gruppe (Play/Pause · Stop · Tempo). */
export function TtsQuick() {
  if (!getToken()) return null;
  const pos = ttsSpeedPos.value;
  const playing = ttsPlaying.value && !ttsWaiting.value;
  const rate = rateFromPos(pos).toFixed(1).replace(".", ",");
  return (
    <span class="tts-group" role="group" aria-label="Vorlesen">
      <button
        type="button"
        class={`tts-quick${playing ? " spielt" : ""}${ttsWaiting.value ? " wartet" : ""}`}
        aria-label={playing ? "Pause" : "Vorlesen — liest automatisch weiter"}
        title={playing ? "Pause — Stelle wird gemerkt" : "Vorlesen — liest automatisch weiter"}
        onClick={headerPlay}
      >
        <span aria-hidden="true">{playGlyph()}</span>
      </button>
      <button
        type="button"
        class="tts-quick tts-quick-stop"
        aria-label="Vorlesen stoppen"
        title="Vorlesen stoppen"
        onClick={headerStop}
      >
        <span aria-hidden="true">■</span>
      </button>
      <button
        type="button"
        class="tts-speed-btn"
        aria-label={`Vorlese-Tempo ${rate}-fach — tippen für nächste Stufe`}
        title={speedTip(pos)}
        onClick={headerSpeedCycle}
      >
        {rate}<span class="tts-speed-x" aria-hidden="true">×</span>
      </button>
    </span>
  );
}
